import React, { useState, useEffect } from 'react';
import { ShoppingCart, Plus, Minus, X, Search, CheckCircle, Clock, Send, DollarSign, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PosOrderEntry = () => {
  const [tables, setTables] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [currentOrder, setCurrentOrder] = useState({
    items: [],
    subtotal: 0,
    tax_amount: 0,
    total_amount: 0,
    customer_name: '',
    party_size: 1,
    server_name: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    payment_method: 'card',
    amount: 0,
    tip_amount: 0
  });

  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('current_organization_id');
  const locationId = localStorage.getItem('current_location_id');

  useEffect(() => {
    fetchTables();
    fetchMenuItems();
  }, []);

  useEffect(() => {
    // Recalculate totals when items change
    const subtotal = currentOrder.items.reduce((sum, item) => sum + item.subtotal, 0);
    const tax_amount = subtotal * 0.20; // 20% VAT
    const total_amount = subtotal + tax_amount;
    
    setCurrentOrder(prev => ({
      ...prev,
      subtotal,
      tax_amount,
      total_amount
    }));
    
    setPaymentData(prev => ({
      ...prev,
      amount: total_amount
    }));
  }, [currentOrder.items]);

  const fetchTables = async () => {
    try {
      const res = await fetch(`${API_URL}/api/pos/dinein/tables?status=available`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          ...(locationId && { 'X-Location-Id': locationId })
        }
      });
      if (res.ok) {
        const data = await res.json();
        setTables(data);
      }
    } catch (err) {
      console.error('Failed to fetch tables:', err);
    }
  };

  const fetchMenuItems = async () => {
    try {
      const res = await fetch(`${API_URL}/api/menu-items?organization_id=${orgId}${locationId ? `&location_id=${locationId}` : ''}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          ...(locationId && { 'X-Location-Id': locationId })
        }
      });
      if (res.ok) {
        const data = await res.json();
        setMenuItems(data.items || []);
      }
    } catch (err) {
      console.error('Failed to fetch menu items:', err);
    }
  };

  const selectTable = (table) => {
    setSelectedTable(table);
    setCurrentOrder({
      items: [],
      subtotal: 0,
      tax_amount: 0,
      total_amount: 0,
      customer_name: '',
      party_size: 1,
      server_name: localStorage.getItem('user_name') || ''
    });
  };

  const addItemToOrder = (menuItem) => {
    const existingItem = currentOrder.items.find(item => item.menu_item_id === menuItem.id);
    
    if (existingItem) {
      // Increase quantity
      setCurrentOrder(prev => ({
        ...prev,
        items: prev.items.map(item =>
          item.menu_item_id === menuItem.id
            ? {
                ...item,
                quantity: item.quantity + 1,
                subtotal: (item.quantity + 1) * item.unit_price
              }
            : item
        )
      }));
    } else {
      // Add new item
      setCurrentOrder(prev => ({
        ...prev,
        items: [
          ...prev.items,
          {
            menu_item_id: menuItem.id,
            name: menuItem.name,
            quantity: 1,
            unit_price: menuItem.base_price,
            subtotal: menuItem.base_price,
            modifiers: [],
            special_instructions: ''
          }
        ]
      }));
    }
  };

  const updateItemQuantity = (itemId, delta) => {
    setCurrentOrder(prev => ({
      ...prev,
      items: prev.items
        .map(item =>
          item.menu_item_id === itemId
            ? {
                ...item,
                quantity: Math.max(0, item.quantity + delta),
                subtotal: Math.max(0, item.quantity + delta) * item.unit_price
              }
            : item
        )
        .filter(item => item.quantity > 0)
    }));
  };

  const removeItem = (itemId) => {
    setCurrentOrder(prev => ({
      ...prev,
      items: prev.items.filter(item => item.menu_item_id !== itemId)
    }));
  };

  const createOrder = async () => {
    if (!selectedTable) {
      alert('Please select a table first');
      return;
    }
    
    if (currentOrder.items.length === 0) {
      alert('Please add items to the order');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const orderData = {
        table_id: selectedTable.id,
        table_number: selectedTable.table_number,
        server_name: currentOrder.server_name,
        customer_name: currentOrder.customer_name || 'Walk-in',
        party_size: currentOrder.party_size,
        items: currentOrder.items,
        subtotal: currentOrder.subtotal,
        tax_amount: currentOrder.tax_amount,
        total_amount: currentOrder.total_amount,
        status: 'open'
      };

      const res = await fetch(`${API_URL}/api/pos/dinein/orders`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Organization-Id': orgId || '',
          ...(locationId && { 'X-Location-Id': locationId }),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (res.ok) {
        const data = await res.json();
        alert(`Order ${data.order_number} created successfully!`);
        setLastOrder({ id: data.order_id, number: data.order_number });

        // Reset order
        setCurrentOrder({
          items: [],
          subtotal: 0,
          tax_amount: 0,
          total_amount: 0,
          customer_name: '',
          party_size: 1,
          server_name: localStorage.getItem('user_name') || ''
        });
        setSelectedTable(null);
        fetchTables(); // Refresh tables
      } else {
        const errData = await res.json();
        setError(errData.detail || 'Failed to create order');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredMenuItems = menuItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">POS Order Entry</h1>
        <p className="text-gray-600">Take dine-in orders and manage tables</p>
        {lastOrder && (
          <a className="text-sm text-blue-600 underline" href={`/ck-ops?trace_order=${encodeURIComponent(lastOrder.id)}`}>
            Trace order {lastOrder.number} (lots used are recorded when the order is closed)
          </a>
        )}
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Table Selection & Order Details */}
        <div className="lg:col-span-1 space-y-4">
          {/* Table Selection */}
          <div className="bg-white shadow rounded-lg p-4">
            <h2 className="text-lg font-semibold mb-4">Select Table</h2>
            <div className="grid grid-cols-2 gap-2">
              {tables.map(table => (
                <button
                  key={table.id}
                  onClick={() => selectTable(table)}
                  className={`p-3 rounded-lg border-2 transition-all ${
                    selectedTable?.id === table.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="font-semibold">Table {table.table_number}</div>
                  <div className="text-sm text-gray-500">{table.capacity} seats</div>
                </button>
              ))}
            </div>
          </div>

          {/* Order Details */}
          {selectedTable && (
            <div className="bg-white shadow rounded-lg p-4">
              <h2 className="text-lg font-semibold mb-4">Order Details</h2>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
                  <input aria-label="Customer Name"
                    type="text"
                    value={currentOrder.customer_name}
                    onChange={(e) => setCurrentOrder(prev => ({ ...prev, customer_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Walk-in"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Party Size</label>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentOrder(prev => ({ ...prev, party_size: Math.max(1, prev.party_size - 1) }))}
                      className="p-1 border border-gray-300 rounded"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      value={currentOrder.party_size}
                      onChange={(e) => setCurrentOrder(prev => ({ ...prev, party_size: parseInt(e.target.value) || 1 }))}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center"
                      min="1"
                    />
                    <button
                      onClick={() => setCurrentOrder(prev => ({ ...prev, party_size: prev.party_size + 1 }))}
                      className="p-1 border border-gray-300 rounded"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Server</label>
                  <input aria-label="Server"
                    type="text"
                    value={currentOrder.server_name}
                    onChange={(e) => setCurrentOrder(prev => ({ ...prev, server_name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    placeholder="Server name"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Order Summary */}
          {currentOrder.items.length > 0 && (
            <div className="bg-white shadow rounded-lg p-4">
              <h2 className="text-lg font-semibold mb-4">Order Summary</h2>
              
              <div className="space-y-2 mb-4">
                {currentOrder.items.map(item => (
                  <div key={item.menu_item_id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex-1">
                      <div className="font-medium">{item.name}</div>
                      <div className="text-sm text-gray-500">£{item.unit_price.toFixed(2)} × {item.quantity}</div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="font-semibold">£{item.subtotal.toFixed(2)}</span>
                      <button
                        onClick={() => updateItemQuantity(item.menu_item_id, -1)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removeItem(item.menu_item_id)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t pt-3 space-y-1">
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>£{currentOrder.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Tax (20%)</span>
                  <span>£{currentOrder.tax_amount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>Total</span>
                  <span>£{currentOrder.total_amount.toFixed(2)}</span>
                </div>
              </div>

              <button
                onClick={createOrder}
                disabled={loading}
                className="w-full mt-4 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Create Order</span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Right: Menu Items */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg p-4">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search menu items..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {!selectedTable && (
              <div className="text-center py-12 text-gray-500">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Select a table to start taking orders</p>
              </div>
            )}

            {selectedTable && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {filteredMenuItems.map(item => (
                  <button
                    key={item.id}
                    onClick={() => addItemToOrder(item)}
                    className="p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left"
                  >
                    <div className="font-semibold mb-1">{item.name}</div>
                    <div className="text-sm text-gray-500 mb-2">{item.description}</div>
                    <div className="text-lg font-bold text-blue-600">£{item.base_price.toFixed(2)}</div>
                    {!item.available && (
                      <div className="text-xs text-red-600 mt-1">Unavailable</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {filteredMenuItems.length === 0 && selectedTable && (
              <div className="text-center py-12 text-gray-500">
                <p>No menu items found. Add items in Menu Management.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PosOrderEntry;
